/**
 * CustomerCreateModal - CANONICAL customer creation modal
 * Used across the entire app: Estimates, Invoices, Jobs, Customers page
 * 
 * Purpose:
 * - Create a customer
 * - Capture the FIRST (primary) service address only
 * 
 * NOT for: Managing multiple addresses (use Customer Edit for that)
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { User, MapPin } from "phosphor-react";
import {
  normalizeEmail,
  isValidEmail,
  getEmailError,
  normalizeUsPhone,
  isValidUsPhone,
  getPhoneError
} from "../utils/contactValidation";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const initialFormState = {
  name: "",
  phone: "",
  email: "",
  // Address fields (primary address only)
  street: "",
  address_line2: "",
  city: "",
  state: "",
  zip: "",
};

export default function CustomerCreateModal({ isOpen, onClose, onCustomerCreated }) {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFieldErrors({});
    const errors = {};
    
    // Validation
    if (!formData.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!formData.phone.trim() && !formData.email.trim()) {
      toast.error("Phone or email is required");
      return;
    }
    if (!formData.street.trim() || !formData.city.trim() || !formData.state.trim() || !formData.zip.trim()) {
      toast.error("Address (street, city, state, zip) is required");
      return;
    }
    
    // Email validation
    const normalizedEmail = normalizeEmail(formData.email);
    if (normalizedEmail && !isValidEmail(normalizedEmail)) {
      const emailError = getEmailError(normalizedEmail);
      errors.email = emailError || "Please enter a valid email like name@example.com";
      toast.error(errors.email);
    }
    
    // Phone validation
    const phoneInput = formData.phone.trim();
    if (phoneInput) {
      if (!isValidUsPhone(phoneInput)) {
        const phoneError = getPhoneError(phoneInput);
        errors.phone = phoneError || "Please enter a 10-digit US phone number";
        toast.error(errors.phone);
      }
    }
    
    // If any errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    // Normalize values before sending
    const normalizedPhone = normalizeUsPhone(phoneInput);

    setLoading(true);
    try {
      const response = await fetch(`${API}/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: normalizedPhone || null,
          email: normalizedEmail || null,
          // Preferred communication defaults to EMAIL (not shown to user)
          preferred_communication: "email",
          addresses: [
            {
              label: "Primary",
              street: formData.street.trim(),
              address_line2: formData.address_line2.trim(),
              city: formData.city.trim(),
              state: formData.state.trim(),
              zip: formData.zip.trim(),
              is_primary: true,
            },
          ],
        }),
      });

      if (response.ok) {
        const customer = await response.json();
        toast.success("Customer created successfully");
        
        // Notify onboarding system about customer creation (fire and forget)
        fetch(`${API}/onboarding/mark-customer-created`, {
          method: "POST",
          credentials: "include",
        }).catch(() => {}); // Ignore errors - onboarding update is not critical
        
        onCustomerCreated(customer);
        setFormData(initialFormState);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create customer");
      }
    } catch (error) {
      toast.error("Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialFormState);
    setFieldErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent data-testid="customer-create-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
            <User size={20} weight="bold" />
            New Customer
          </DialogTitle>
        </DialogHeader>
        
        <DialogBody>
          <form id="customer-create-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Info */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="name">Customer Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="mt-1 h-11 border-2"
                  required
                  data-testid="customer-create-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className={`mt-1 h-11 border-2 ${fieldErrors.phone ? 'border-red-500' : ''}`}
                    data-testid="customer-create-phone"
                  />
                  {fieldErrors.phone && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={`mt-1 h-11 border-2 ${fieldErrors.email ? 'border-red-500' : ''}`}
                    data-testid="customer-create-email"
                  />
                  {fieldErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-zinc-500">* Phone or email required</p>
            </div>

            {/* Service Address Section */}
            <div className="pt-3 border-t border-zinc-200">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700">Service Address *</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    placeholder="123 Main St"
                    className="mt-1 h-11 border-2"
                    required
                    data-testid="customer-create-street"
                  />
                </div>
                <div>
                  <Label htmlFor="address_line2">Apt/Suite/Unit</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleChange}
                    placeholder="Apt 4B"
                    className="mt-1 h-11 border-2"
                    data-testid="customer-create-line2"
                  />
                </div>
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Boston"
                      className="mt-1 h-11 border-2"
                      required
                      data-testid="customer-create-city"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="MA"
                      maxLength={2}
                      className="mt-1 h-11 border-2 uppercase"
                      required
                      data-testid="customer-create-state"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="zip">ZIP *</Label>
                    <Input
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      placeholder="02101"
                      maxLength={10}
                      className="mt-1 h-11 border-2"
                      required
                      data-testid="customer-create-zip"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none border-2"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="customer-create-form"
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
            disabled={loading}
            data-testid="customer-create-save"
          >
            {loading ? "Creating..." : "Create Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export with alias for backwards compatibility during transition
export { CustomerCreateModal as QuickCustomerModal };
